import { useState, useRef, type ChangeEvent, type DragEvent } from 'react'
import { uploadToR2 } from '../services/r2Service'
import { compressImageToUnder100KB, formatFileSize } from '../utils/imageCompressor'
import ImageCropModal from './ImageCropModal'

interface ImageUploadProps {
  value: string
  onChange: (url: string) => void
  folder?: string
  label?: string
  accept?: string
  allowPdf?: boolean
  cropAspectRatio?: number
  cropOutputWidth?: number
  cropOutputHeight?: number
}

export default function ImageUpload({
  value,
  onChange,
  folder = 'uploads',
  accept = 'image/*,.pdf',
  allowPdf = true,
  cropAspectRatio,
  cropOutputWidth = 1600,
  cropOutputHeight = 500
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [compressing, setCompressing] = useState(false)
  const [infoMessage, setInfoMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [pendingCropFile, setPendingCropFile] = useState<File | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const processImageFile = async (file: File) => {
    let fileToUpload = file
    setCompressing(true)
    try {
      const result = await compressImageToUnder100KB(file, 100 * 1024)
      fileToUpload = result.file
      if (result.isCompressed) {
        setInfoMessage(`⚡ কমপ্রেসড: ${formatFileSize(result.originalSize)} ➔ ${formatFileSize(result.compressedSize)} (< 100 KB)`)
      } else if (result.compressedSize <= 100 * 1024) {
        setInfoMessage(`✅ সাইজ: ${formatFileSize(result.compressedSize)} (< 100 KB)`)
      }
    } catch (e: any) {
      console.warn('Image compression warning:', e)
    } finally {
      setCompressing(false)
    }

    setUploading(true)
    try {
      const url = await uploadToR2(fileToUpload, folder)
      onChange(url)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setUploading(false)
    }
  }

  const processPdfFile = async (file: File) => {
    let fileToUpload = file
    setCompressing(true)
    try {
      const { compressPdfPagesToUnder80KB } = await import('../utils/pdfCompressor')
      const result = await compressPdfPagesToUnder80KB(file, 80 * 1024)
      fileToUpload = result.file
      if (result.isCompressed) {
        setInfoMessage(`⚡ PDF কমপ্রেসড: ${formatFileSize(result.originalSize)} ➔ ${formatFileSize(result.compressedSize)} (${result.pageCount} পাতা, প্রতি পাতা < 80 KB)`)
      } else {
        setInfoMessage(`✅ সাইজ: ${formatFileSize(result.compressedSize)} (${result.pageCount} পাতা, ইতিমধ্যে < 80 KB/পাতা)`)
      }
    } catch (e: any) {
      console.warn('PDF compression warning:', e)
      setError('PDF কমপ্রেস করতে সমস্যা হয়েছে, মূল ফাইলটি আপলোড করা হচ্ছে।')
    } finally {
      setCompressing(false)
    }

    setUploading(true)
    try {
      const url = await uploadToR2(fileToUpload, folder)
      onChange(url)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setUploading(false)
    }
  }

  const handleFile = async (file: File) => {
    setError(null)
    setInfoMessage(null)
    
    // Strict restriction: Block all videos explicitly. Only images and PDF documents are allowed
    const isVideo = file.type.startsWith('video/') || /\.(mp4|mkv|webm|avi|mov|flv|wmv)$/i.test(file.name)
    const isImage = file.type.startsWith('image/')
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')

    if (isVideo) {
      setError('ভিডিও ফাইল আপলোড করার অনুমতি নেই! শুধুমাত্র ছবি এবং PDF আপলোড করতে পারবেন।')
      return
    }

    if (!isImage && (!allowPdf || !isPdf)) {
      setError(allowPdf
        ? 'শুধুমাত্র ছবি (JPG, PNG, WebP) এবং PDF ফাইল আপলোড করা যাবে!'
        : 'শুধুমাত্র ছবি (JPG, PNG, WebP) আপলোড করা যাবে!')
      return
    }

    if (isImage && cropAspectRatio) {
      setPendingCropFile(file)
      return
    }

    if (isPdf) {
      await processPdfFile(file)
      return
    }

    await processImageFile(file)
  }


  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const isImage = value && !value.endsWith('.pdf') && !value.endsWith('.mp3')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {/* Drop zone / trigger */}
      <div
        onClick={() => !uploading && !compressing && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          border: `2px dashed ${dragOver ? '#ea580c' : error ? '#ef4444' : '#fdba74'}`,
          borderRadius: '10px',
          padding: '14px 12px',
          cursor: uploading || compressing ? 'wait' : 'pointer',
          background: dragOver ? '#fff7ed' : '#fff',
          transition: 'all 0.15s ease',
          textAlign: 'center',
          minHeight: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          flexDirection: 'column'
        }}
      >
        {compressing ? (
          <>
            <div style={{
              width: '24px', height: '24px',
              border: '3px solid #e5e7eb', borderTop: '3px solid #ea580c',
              borderRadius: '50%', animation: 'spin 0.7s linear infinite'
            }} />
            <span style={{ fontSize: '12px', color: '#ea580c', fontWeight: 600 }}>
              ফাইল কমপ্রেস করা হচ্ছে... (ছবি: &lt;100 KB, PDF: প্রতি পাতা &lt;80 KB)
            </span>
          </>
        ) : uploading ? (
          <>
            <div style={{
              width: '24px', height: '24px',
              border: '3px solid #e5e7eb', borderTop: '3px solid #6366f1',
              borderRadius: '50%', animation: 'spin 0.7s linear infinite'
            }} />
            <span style={{ fontSize: '12px', color: '#64748b' }}>Uploading to R2...</span>
          </>
        ) : (
          <>
            <span style={{ fontSize: '20px' }}>{value ? '✅' : '☁️'}</span>
            <span style={{ fontSize: '12px', color: '#64748b' }}>
              {value ? 'Click to replace' : 'Click or drag to upload (Auto <100KB High-Res)'}
            </span>
          </>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={onInputChange}
      />

      {/* URL field (manual paste also works) */}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Or paste URL directly..."
        style={{
          width: '100%',
          padding: '7px 10px',
          borderRadius: '7px',
          border: '1.5px solid #e2e8f0',
          fontSize: '12px',
          color: '#475569',
          boxSizing: 'border-box'
        }}
      />

      {/* Preview */}
      {isImage && (
        <div style={{ position: 'relative', width: '100%', height: '110px', marginTop: '4px' }}>
          <img
            src={value}
            alt="preview"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px',
              border: '1px solid #fed7aa'
            }}
            onError={(e) => (e.currentTarget.style.display = 'none')}
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange('') }}
            style={{
              position: 'absolute', top: '6px', right: '6px',
              background: 'rgba(0,0,0,0.65)', border: 'none', borderRadius: '50%',
              width: '22px', height: '22px', color: '#fff', cursor: 'pointer',
              fontSize: '12px', lineHeight: '22px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >✕</button>
        </div>
      )}

      {infoMessage && (
        <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: 600 }}>{infoMessage}</span>
      )}

      {error && (
        <span style={{ fontSize: '11px', color: '#ef4444' }}>⚠️ {error}</span>
      )}

      {pendingCropFile && cropAspectRatio && (
        <ImageCropModal
          file={pendingCropFile}
          aspectRatio={cropAspectRatio}
          outputWidth={cropOutputWidth}
          outputHeight={cropOutputHeight}
          onCancel={() => setPendingCropFile(null)}
          onConfirm={(croppedFile) => {
            setPendingCropFile(null)
            processImageFile(croppedFile)
          }}
        />
      )}

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
